import { DateTime } from "luxon-business-days";

function waitForGeoData(timeout = 10000) {
  // default timeout of 10000 milliseconds (10 seconds)
  const isObjectNotEmpty = (obj) => {
    return obj && Object.keys(obj).length > 0;
  };

  return new Promise((resolve, reject) => {
    const startTime = Date.now();
    let checkInterval = setInterval(() => {
      if (typeof Geolizr !== "undefined" && isObjectNotEmpty(Geolizr.geoData)) {
        clearInterval(checkInterval);
        resolve(Geolizr.geoData);
      } else if (Date.now() - startTime > timeout) {
        clearInterval(checkInterval);
        reject(new Error("Timeout waiting for Geolizr.geoData"));
      }
    }, 100); // check every 100 milliseconds
  });
}

async function processGeoData() {
  try {
    const geolizrData = await waitForGeoData();
    const geolizrUserLocation = extractUserLocation(geolizrData);

    if (geolizrUserLocation) {
      updateLocalStorageWithUserLocation(geolizrUserLocation);
    }
  } catch (error) {
    console.error("Error waiting for GeoData:", error);
  }
}

function extractUserLocation(geolizrData) {
  // check if geolizrData is valid and is not empty
  if (
    geolizrData &&
    Object.keys(geolizrData).length > 0 &&
    geolizrData.countryCode
  ) {
    return {
      country: {
        code: geolizrData.country.code,
        country: geolizrData.country.country,
      },
      countryCode: geolizrData.countryCode,
      city: geolizrData.city,
      regionName: geolizrData.regionName,
      region: geolizrData.region,
      zip: geolizrData.zip,
      expiresAt: new Date().getTime() + 1000 * 60 * 60 * 24 * 1, // 1 day
    };
  }

  return null;
}

function updateLocalStorageWithUserLocation(userLocation, forceUpdate = false) {
  const storedLocationString = localStorage.getItem("userStoredLocation");
  const storedLocation = storedLocationString
    ? JSON.parse(storedLocationString)
    : null;

  const userLocationUpdatedEvent = new CustomEvent("user_location_updated", {
    detail: userLocation,
  });

  if (forceUpdate) {
    // Wrap localStorage.setItem in a try-catch block to handle QuotaExceededError
    try {
      localStorage.setItem("userStoredLocation", JSON.stringify(userLocation));
    } catch (e) {
      if (e.name === "QuotaExceededError") {
        console.warn("Quota exceeded while setting userStoredLocation in localStorage.");
      } else {
        throw e;
      }
    }
    dispatchEvent(userLocationUpdatedEvent);
  } else if (storedLocation === null) {
    // If no location is stored, and the current location's currency is supported, store it
    if (CURRENCY.includes(userLocation.countryCode)) {
      // Wrap localStorage.setItem in a try-catch block to handle QuotaExceededError
      try {
        localStorage.setItem("userStoredLocation", JSON.stringify(userLocation));
      } catch (e) {
        if (e.name === "QuotaExceededError") {
          console.warn("Quota exceeded while setting userStoredLocation in localStorage.");
        } else {
          throw e;
        }
      }
      dispatchEvent(userLocationUpdatedEvent);
    }
  } else {
    // Check if the currency matches but the location is different
    if (CURRENCY.includes(userLocation.countryCode)) {
      const isLocationDifferent = Object.keys(userLocation).some(
        (key) => userLocation[key] !== storedLocation[key]
      );
      const isExpired = new Date(storedLocation.expiresAt) < new Date();

      if (isLocationDifferent && isExpired) {
        // Wrap localStorage.setItem in a try-catch block to handle QuotaExceededError
        try {
          localStorage.setItem(
            "userStoredLocation",
            JSON.stringify(userLocation)
          );
        } catch (e) {
          if (e.name === "QuotaExceededError") {
            console.warn("Quota exceeded while setting userStoredLocation in localStorage.");
          } else {
            throw e;
          }
        }
        dispatchEvent(userLocationUpdatedEvent);
      }
    } else {
      // If stored location's currency no longer supported, remove it
      localStorage.removeItem("userStoredLocation");
    }
  }
  setEddFromMetafields();
}

processGeoData();

function findBestShippingLocation() {
  const shippingLocations = JSON.parse(localStorage.getItem("shippingMetaObj"));
  const storedLocation = localStorage.getItem("userStoredLocation");
  const userLocation = storedLocation ? JSON.parse(storedLocation) : null;
  if (!userLocation || !shippingLocations) return null;
  // Filter by country
  let filteredLocations = shippingLocations.filter(
    (location) => location.country_code === userLocation.countryCode
  );
  // Find locations with matching cities and region codes, if any
  let cityAndRegionMatches = filteredLocations.filter(
    (location) =>
      location.cities?.includes(userLocation.city) &&
      location.region_codes?.includes(userLocation.region)
  );
  if (cityAndRegionMatches.length > 0) {
    // Return the match with the shortest standard shipping time
    return cityAndRegionMatches.reduce((prev, current) =>
      prev.min_time_standard < current.min_time_standard ? prev : current
    );
  }
  // If no city and region match, find locations with matching region codes
  let regionMatches = filteredLocations.filter((location) =>
    location.region_codes?.includes(userLocation.region)
  );
  if (regionMatches.length > 0) {
    // Return the region match with the shortest standard shipping time
    return regionMatches.reduce((prev, current) =>
      prev.min_time_standard < current.min_time_standard ? prev : current
    );
  }
  // If no city or region matches, return the most generic location (no cities or region codes specified)
  let genericLocation = filteredLocations.find(
    (location) => !location.cities && !location.region_codes
  );
  if (genericLocation) {
    return genericLocation;
  }
  // If no generic location, return null or handle as required
  return null;
}

function setEddFromMetafields(additionalDay = 0) {
  const shippingZone = findBestShippingLocation();
  if (!shippingZone) {
    localStorage.setItem("edd", null);
    return;
  }

  const avgOfStdDays = Math.ceil(
    (shippingZone.min_time_standard + shippingZone.max_time_standard) / 2
  );
  const avgOfExpDays = shippingZone.min_time_expedited
    ? Math.ceil(
        (shippingZone.min_time_expedited + shippingZone.max_time_expedited) / 2
      )
    : null;
  const avgOfPriorityDays = shippingZone?.min_time_priority
    ? Math.ceil(
        (shippingZone.min_time_priority + shippingZone.max_time_priority) / 2
      )
    : null;
  const avgProcessingTime = 2;

  let dateToCalculate = DateTime.local().plus({ days: additionalDay });
  const result = shippingZone
    ? {
        eddBestExpress: dateToCalculate
          .plusBusiness({
            days: avgProcessingTime + shippingZone.min_time_expedited,
          })
          .toLocaleString({ ...DateTime.DATE_MED, weekday: 'long' }),
        eddWorstExpress: dateToCalculate
          .plusBusiness({
            days: avgProcessingTime + shippingZone.max_time_expedited,
          })
          .toLocaleString({ ...DateTime.DATE_MED, weekday: 'long' }),
        eddAvgExpress: dateToCalculate
          .plusBusiness({ days: avgProcessingTime + avgOfExpDays })
          .toLocaleString({ ...DateTime.DATE_MED, weekday: 'long' }),
        eddBestStandard: dateToCalculate
          .plusBusiness({
            days: avgProcessingTime + shippingZone.min_time_standard,
          })
          .toLocaleString({ ...DateTime.DATE_MED, weekday: 'long' }),
        eddWorstStandard: dateToCalculate
          .plusBusiness({
            days: avgProcessingTime + shippingZone.max_time_standard,
          })
          .toLocaleString({ ...DateTime.DATE_MED, weekday: 'long' }),
        eddAvgStandard: dateToCalculate
          .plusBusiness({ days: avgProcessingTime + avgOfStdDays })
          .toLocaleString({ ...DateTime.DATE_MED, weekday: 'long' }),
        eddBestPriority: dateToCalculate
          .plusBusiness({
            days: avgProcessingTime + shippingZone.min_time_priority,
          })
          .toLocaleString({ ...DateTime.DATE_MED, weekday: 'long' }),
        eddWorstPriority: dateToCalculate
          .plusBusiness({
            days: avgProcessingTime + shippingZone.max_time_priority,
          })
          .toLocaleString({ ...DateTime.DATE_MED, weekday: 'long' }),
        eddAvgPriority: dateToCalculate
          .plusBusiness({ days: avgProcessingTime + avgOfPriorityDays })
          .toLocaleString({ ...DateTime.DATE_MED, weekday: 'long' }),
      }
    : null;

  localStorage.setItem("edd", JSON.stringify(result));
  // dispatch edd_updated event
  const eddUpdatedEvent = new CustomEvent("edd_updated", { detail: result });
  dispatchEvent(eddUpdatedEvent);
}

window.addEventListener("edd_events", (e) => {
  let event = e.detail;
  let actions = {
    edd_update_user_location: updateLocalStorageWithUserLocation,
    edd_update_shipping: setEddFromMetafields,
  };
  if (actions[event.action]) {
    if (event.action === "edd_update_user_location" && event.data) {
      actions[event.action](event.data, true);
    } else if (event.action === "edd_update_shipping" && event.data) {
      actions[event.action](event.data);
    } else {
      actions[event.action]();
    }
  }
});
